"use client"

import { useState, useMemo } from "react"
import { Plus, Search, Edit, Save, History, Filter, BarChart3, MoreVertical, Home, Layers, PieChart, Trash2 } from "lucide-react"
import { Button } from "@/templates/Admin/components/Button/Button"
import { Input } from "@/templates/Admin/components/Input/Input"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/templates/Admin/components/Dialog/Dialog"
import { Label } from "@/templates/Admin/components/Label/Label"
import { Textarea } from "@/templates/Admin/components/Textarea/Textarea"
import LayoutDefault from "@/templates/Admin/variants/default/layouts/LayoutDefault"

interface RoomProps {
  isOpen?: boolean
  onClose?: () => void
  isSidebarOpen?: boolean
  isEmbedded?: boolean
}

export default function Room({ isOpen = true, onClose = () => {}, isSidebarOpen = true, isEmbedded = false }: RoomProps) {
  // Toast notifications logger
  const toast = ({ title, description }: { title: string; description: string; variant?: string }) => {
    console.log(`${title}: ${description}`)
  }

  // State Management
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(1) // Initial: Room 1 (Kho A)
  const [selectedShelfId, setSelectedShelfId] = useState<number | null>(2) // Initial: Shelf 2 (Tủ 02)

  // Rooms Data - Match Image (4 Warehouses)
  const [rooms, setRooms] = useState([
    {
      id: 1,
      name: "Kho A - Tầng",
      cabinets: 6,
      shelves: 16,
      color: "bg-green-500",
    },
    {
      id: 2,
      name: "Kho B - Tầng",
      cabinets: 4,
      shelves: 8,
      color: "bg-yellow-500",
    },
    {
      id: 3,
      name: "Kho C - Tầng",
      cabinets: 2,
      shelves: 3,
      color: "bg-blue-500",
    },
    {
      id: 4,
      name: "Kho D - Tầng",
      cabinets: 1,
      shelves: 1,
      color: "bg-purple-500",
    }
  ])

  // Shelves / Inventory Data - Math Unified to Sum up perfectly
  const [shelves, setShelves] = useState([
    // Kho A
    {
      id: 1,
      location: "Kho A / Tủ 01 / Kệ 01",
      roomId: 1,
      type: "Tài liệu giấy",
      itemsCount: 50,
      capacity: 100,
      status: "Còn chỗ",
    },
    {
      id: 2,
      location: "Kho A / Tủ 01 / Kệ 02",
      roomId: 1,
      type: "Bản đồ cổ",
      itemsCount: 85,
      capacity: 100,
      status: "Gần đầy",
    },
    {
      id: 3,
      location: "Kho A / Tủ 01 / Kệ 03",
      roomId: 1,
      type: "Gốm sứ",
      itemsCount: 18,
      capacity: 100,
      status: "Còn chỗ",
    },
    {
      id: 4,
      location: "Kho A / Tủ 02 / Kệ 01",
      roomId: 1,
      type: "Tranh sơn dầu",
      itemsCount: 100,
      capacity: 100,
      status: "Đầy",
    },
    {
      id: 5,
      location: "Kho A / Tủ 02 / Kệ 02",
      roomId: 1,
      type: "Hiện vật kim loại",
      itemsCount: 87,
      capacity: 100,
      status: "Gần đầy",
    },
    // Kho B
    {
      id: 6,
      location: "Kho B / Tủ 03 / Kệ 01",
      roomId: 2,
      type: "Sách lật cổ",
      itemsCount: 95,
      capacity: 100,
      status: "Gần đầy",
    },
    {
      id: 7,
      location: "Kho B / Tủ 03 / Kệ 02",
      roomId: 2,
      type: "Hiện vật gỗ",
      itemsCount: 100,
      capacity: 100,
      status: "Đầy",
    },
    {
      id: 8,
      location: "Kho B / Tủ 04 / Kệ 01",
      roomId: 2,
      type: "Băng đĩa âm thanh",
      itemsCount: 9,
      capacity: 20,
      status: "Còn chỗ",
    },
    {
      id: 9,
      location: "Kho B / Tủ 04 / Kệ 02",
      roomId: 2,
      type: "Đồ gỗ",
      itemsCount: 6,
      capacity: 10,
      status: "Còn chỗ",
    },
    // Kho C
    {
      id: 10,
      location: "Kho C / Tủ 05 / Kệ 01",
      roomId: 3,
      type: "Văn bản số hóa",
      itemsCount: 45,
      capacity: 50,
      status: "Đầy",
    },
    // Kho D
    {
      id: 11,
      location: "Kho D / Tủ 06 / Kệ 01",
      roomId: 4,
      type: "Hiện vật khác",
      itemsCount: 5,
      capacity: 50,
      status: "Còn chỗ",
    }
  ])

  // Dynamic calculations to make data consistent & correct
  const roomsWithCalculatedData = useMemo(() => {
    return rooms.map(room => {
      const roomShelves = shelves.filter(s => s.roomId === room.id)
      const itemsCount = roomShelves.reduce((sum, s) => sum + s.itemsCount, 0)
      const capacity = roomShelves.reduce((sum, s) => sum + (s.capacity || 100), 0)
      const usage = capacity > 0 ? parseFloat(((itemsCount / capacity) * 100).toFixed(1)) : 0
      
      let status = "Còn chỗ"
      if (usage >= 90) status = "Đầy"
      else if (usage >= 75) status = "Gần đầy"

      return {
        ...room,
        itemsCount,
        capacity,
        usage,
        status
      }
    })
  }, [rooms, shelves])

  const stats = useMemo(() => {
    const totalRooms = rooms.length
    const totalShelves = rooms.reduce((sum, r) => sum + r.shelves, 0)
    const totalItems = shelves.reduce((sum, s) => sum + s.itemsCount, 0)
    const totalCapacity = shelves.reduce((sum, s) => sum + (s.capacity || 100), 0)
    const averageUsage = totalCapacity > 0 ? Math.round((totalItems / totalCapacity) * 100) : 0

    return {
      totalRooms,
      totalShelves,
      averageUsage
    }
  }, [rooms, shelves])

  // Modal & Form states
  const [isCreateRoomOpen, setIsCreateRoomOpen] = useState(false)
  const [roomForm, setRoomForm] = useState({
    name: "",
    code: "",
    manager: "",
    cabinets: 0,
    shelves: 0,
    description: "",
  })

  // Action toggles & filters
  const [showStats, setShowStats] = useState(true)
  const [showFilters, setShowFilters] = useState(false)
  const [statusFilter, setStatusFilter] = useState("All")
  const [typeFilter, setTypeFilter] = useState("All")
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  // Shelf operations states
  const [isCreateShelfOpen, setIsCreateShelfOpen] = useState(false)
  const [shelfForm, setShelfForm] = useState({
    location: "",
    type: "Tài liệu giấy",
    itemsCount: 0,
    capacity: 100,
    status: "Còn chỗ" as "Còn chỗ" | "Đầy" | "Gần đầy"
  })

  const [isEditShelfOpen, setIsEditShelfOpen] = useState(false)
  const [editingShelf, setEditingShelf] = useState<any>(null)

  // Filtered data based on search and selected room + filters
  const filteredShelves = useMemo(() => {
    return shelves.filter((shelf) => {
      const matchesRoom = selectedRoomId ? shelf.roomId === selectedRoomId : true
      const matchesStatus = statusFilter === "All" ? true : shelf.status === statusFilter
      const matchesType = typeFilter === "All" ? true : shelf.type === typeFilter
      const matchesSearch = searchQuery
        ? shelf.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
          shelf.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
          shelf.status.toLowerCase().includes(searchQuery.toLowerCase())
        : true
      return matchesRoom && matchesStatus && matchesType && matchesSearch
    })
  }, [shelves, selectedRoomId, searchQuery, statusFilter, typeFilter])

  // CRUD Operations
  const createRoom = () => {
    if (!roomForm.name.trim()) {
      toast({
        title: "Lỗi",
        description: "Tên kho không được để trống",
        variant: "destructive",
      })
      return
    }

    const cabinetsCount = Number(roomForm.cabinets) || 0
    const shelvesCount = Number(roomForm.shelves) || 0

    const newRoom = {
      id: rooms.length + 1,
      name: roomForm.name,
      cabinets: cabinetsCount,
      shelves: shelvesCount,
      color: "bg-blue-500",
    }

    setRooms([...rooms, newRoom])

    // Automatically generate shelves data for this room
    if (shelvesCount > 0) {
      const generatedShelves = []
      for (let i = 1; i <= shelvesCount; i++) {
        const cabinetNum = Math.ceil(i / 4).toString().padStart(2, '0')
        const shelfNum = ((i - 1) % 4 + 1).toString().padStart(2, '0')
        generatedShelves.push({
          id: shelves.length + i,
          location: `${roomForm.name} / Tủ ${cabinetNum} / Kệ ${shelfNum}`,
          roomId: newRoom.id,
          type: "Chưa phân loại",
          itemsCount: 0,
          capacity: 100,
          status: "Còn chỗ" as const,
        })
      }
      setShelves([...shelves, ...generatedShelves])
    }

    setRoomForm({
      name: "",
      code: "",
      manager: "",
      cabinets: 0,
      shelves: 0,
      description: "",
    })
    setIsCreateRoomOpen(false)

    toast({
      title: "Thành công",
      description: `Đã tạo ${newRoom.name} với ${cabinetsCount} tủ và ${shelvesCount} kệ`,
    })
  }

  const createShelf = () => {
    if (!shelfForm.location.trim()) {
      toast({
        title: "Lỗi",
        description: "Vị trí không được để trống",
        variant: "destructive",
      })
      return
    }

    const newShelf = {
      id: shelves.length + 1,
      location: shelfForm.location,
      roomId: selectedRoomId || 1,
      type: shelfForm.type,
      itemsCount: Number(shelfForm.itemsCount) || 0,
      capacity: Number(shelfForm.capacity) || 100,
      status: shelfForm.status,
    }

    setShelves([...shelves, newShelf])
    setShelfForm({
      location: "",
      type: "Tài liệu giấy",
      itemsCount: 0,
      capacity: 100,
      status: "Còn chỗ"
    })
    setIsCreateShelfOpen(false)

    toast({
      title: "Thành công",
      description: `Đã thêm kệ tủ mới vào ${selectedRoomId === 1 ? "Kho A" : "Kho B"}`,
    })
  }

  const saveShelf = () => {
    if (!editingShelf) return

    setShelves(shelves.map(s => s.id === editingShelf.id ? editingShelf : s))
    setIsEditShelfOpen(false)
    setEditingShelf(null)

    toast({
      title: "Thành công",
      description: "Đã cập nhật thông tin kệ tủ",
    })
  }

  const deleteShelf = (id: number) => {
    setShelves(shelves.filter(s => s.id !== id))
    setIsEditShelfOpen(false)
    setEditingShelf(null)

    toast({
      title: "Thành công",
      description: "Đã xóa kệ tủ",
    })
  }

  if (!isOpen) return null

  return (
    <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen} isEmbedded={isEmbedded}>
      <div className="h-screen overflow-y-auto bg-[#F8F8F8] flex flex-col">
        {/* Header */}
        <header className="resource_header w-full px-3 flex-shrink-0">
          <div className="flex items-center h-full">
            <h1 className="text-lg font-bold uppercase text-black tracking-wide">
              QUẢN LÝ KHO BÃI & VỊ TRÍ LƯU TRỮ
            </h1>
          </div>
        </header>

        {/* Content body matching display manage structure */}
        <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-4 font-sans">
          {/* Toolbar */}
          <div className="flex justify-between items-center gap-3 flex-wrap">
            {/* Left Toolbar */}
            <div className="flex items-center gap-2.5">
              {/* Create Button (Context-Aware) */}
              <button 
                onClick={() => {
                  if (selectedRoomId === null) {
                    setIsCreateRoomOpen(true)
                  } else {
                    setIsCreateShelfOpen(true)
                  }
                }}
                className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#224584] hover:bg-[#1a3566] text-white rounded-xl transition-all font-bold shadow-sm text-xs h-9"
              >
                <Plus className="h-4 w-4 text-white" strokeWidth={2.5} />
                <span>{selectedRoomId === null ? "Thêm kho" : "Thêm tủ / kệ"}</span>
              </button>

              {/* Action buttons matching the image */}
              <button 
                onClick={() => setShowStats(!showStats)}
                className={`w-9 h-9 border rounded-xl flex items-center justify-center transition-all shadow-sm ${
                  showStats ? "bg-[#E9ECF4] border-[#224584] text-[#224584]" : "bg-white border-[#D5DEE8] text-gray-500 hover:bg-gray-50"
                }`}
                title="Hiển thị thống kê"
              >
                <BarChart3 className="w-4 h-4" />
              </button>

              {/* <button 
                onClick={() => setIsHistoryOpen(true)}
                className="w-9 h-9 bg-white border border-[#D5DEE8] rounded-xl flex items-center justify-center hover:bg-gray-50 transition-all shadow-sm text-gray-500"
                title="Lịch sử hoạt động"
              >
                <History className="w-4 h-4" />
              </button> */}

              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`w-9 h-9 border rounded-xl flex items-center justify-center transition-all shadow-sm ${
                  showFilters ? "bg-[#E9ECF4] border-[#224584] text-[#224584]" : "bg-white border-[#D5DEE8] text-gray-500 hover:bg-gray-50"
                }`}
                title="Bộ lọc"
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>

            {/* Search Bar - Right */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Tìm kiếm kho, tủ, kệ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-1.5 w-60 h-9 bg-white border border-[#D5DEE8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#224584]/20 focus:border-[#224584] text-xs text-gray-700 placeholder-gray-400 shadow-sm transition-all"
              />
            </div>
          </div>

          {/* Filter Bar */}
          {showFilters && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex gap-4 items-center text-xs shadow-sm flex-wrap animate-in fade-in duration-200">
              <span className="font-semibold text-[#64748B]">Bộ lọc:</span>
              <div className="flex items-center gap-2">
                <label className="text-gray-500 font-medium">Trạng thái:</label>
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-[#D5DEE8] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#224584] text-xs bg-white"
                >
                  <option value="All">Tất cả</option>
                  <option value="Còn chỗ">Còn chỗ</option>
                  <option value="Gần đầy">Gần đầy</option>
                  <option value="Đầy">Đầy</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-gray-500 font-medium">Loại hiện vật:</label>
                <select 
                  value={typeFilter} 
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="border border-[#D5DEE8] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#224584] text-xs bg-white"
                >
                  <option value="All">Tất cả</option>
                  <option value="Tài liệu giấy">Tài liệu giấy</option>
                  <option value="Bản đồ cổ">Bản đồ cổ</option>
                  <option value="Gốm sứ">Gốm sứ</option>
                  <option value="Tranh sơn dầu">Tranh sơn dầu</option>
                  <option value="Hiện vật kim loại">Hiện vật kim loại</option>
                  <option value="Sách lật cổ">Sách lật cổ</option>
                  <option value="Hiện vật gỗ">Hiện vật gỗ</option>
                  <option value="Băng đĩa âm thanh">Băng đĩa âm thanh</option>
                  <option value="Đồ gỗ">Đồ gỗ</option>
                  <option value="Văn bản số hóa">Văn bản số hóa</option>
                </select>
              </div>
              {(statusFilter !== "All" || typeFilter !== "All") && (
                <button 
                  onClick={() => { setStatusFilter("All"); setTypeFilter("All"); }}
                  className="text-[#224584] font-semibold hover:underline"
                >
                  Xóa bộ lọc
                </button>
              )}
            </div>
          )}

          {/* Breadcrumb Row - Matching Image */}
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 bg-transparent py-0.5">
            <div className="w-6 h-6 rounded-md bg-white border border-[#D5DEE8] flex items-center justify-center text-gray-500 shadow-sm">
              <Home className="w-3.5 h-3.5" />
            </div>
            <span className="cursor-pointer hover:text-gray-900 transition-colors" onClick={() => { setSelectedRoomId(null); setSelectedShelfId(null); }}>
              Kho hiện vật
            </span>
            {selectedRoomId && (
              <>
                <span className="text-gray-400 font-normal">/</span>
                <span 
                  className="cursor-pointer hover:text-gray-900 transition-colors"
                  onClick={() => { setSelectedRoomId(selectedRoomId); setSelectedShelfId(null); }}
                >
                  {selectedRoomId === 1 ? "Kho A" : selectedRoomId === 2 ? "Kho B" : selectedRoomId === 3 ? "Kho C" : selectedRoomId === 4 ? "Kho D" : "Kho mới"}
                </span>
              </>
            )}
            {selectedRoomId && selectedShelfId && (
              <>
                <span className="text-gray-400 font-normal">/</span>
                <span className="text-gray-955 font-bold">
                  {shelves.find(s => s.id === selectedShelfId)?.location.split("/").pop()?.trim() || "Kệ khác"}
                </span>
              </>
            )}
          </div>

          {/* Stat Cards - Matching Image */}
          {showStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-in slide-in-from-top-2 duration-200">
              {/* Card 1: Tổng số kho */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex justify-between items-center shadow-sm">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-[#64748B] mb-1">Tổng số kho</span>
                  <span className="text-2xl font-bold text-[#1E293B]">{stats.totalRooms}</span>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#E9ECF4] flex items-center justify-center text-[#4A5E7A]">
                  <Home className="w-5 h-5" />
                </div>
              </div>

              {/* Card 2: Tổng số tủ / kệ */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex justify-between items-center shadow-sm">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-[#64748B] mb-1">Tổng số tủ / kệ</span>
                  <span className="text-2xl font-bold text-[#1E293B]">{stats.totalShelves}</span>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#E9ECF4] flex items-center justify-center text-[#4A5E7A]">
                  <Layers className="w-5 h-5" />
                </div>
              </div>

              {/* Card 3: Sức chứa đã dùng */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex justify-between items-center shadow-sm">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-[#64748B] mb-1">Sức chứa đã dùng</span>
                  <span className="text-2xl font-bold text-[#1E293B]">{stats.averageUsage}%</span>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#E9ECF4] flex items-center justify-center text-[#4A5E7A]">
                  <PieChart className="w-5 h-5" />
                </div>
              </div>
            </div>
          )}

          {/* Room / Warehouse Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roomsWithCalculatedData.map((room) => {
              const isSelected = selectedRoomId === room.id
              const isFull = room.status === "Gần đầy" || room.status === "Đầy"
              const badgeBg = isFull ? "bg-[#9A5116] text-[#FEF7E0]" : "bg-[#135A42] text-[#E6F4EA]"
              const progressTrackBg = isFull ? "bg-[#FEF7E0]" : "bg-[#E6F4EA]"
              const progressBarBg = isFull ? "bg-[#9A5116]" : "bg-[#135A42]"

              return (
                <div 
                  key={room.id}
                  onClick={() => { setSelectedRoomId(room.id); setSelectedShelfId(null); }}
                  className={`bg-white border-2 rounded-2xl p-4 flex flex-col cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md ${
                    isSelected ? "border-[#224584]" : "border-transparent"
                  }`}
                >
                  <div className="flex justify-between items-start mb-2.5">
                    <h3 className="text-sm font-bold text-[#1E293B]">
                      <span className="text-base font-bold mr-1.5 text-[#64748B]">{room.id}</span>
                      {room.name}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badgeBg}`}>
                      {room.status}
                    </span>
                  </div>

                  <div className="text-xs font-medium text-[#64748B] mb-2">
                    {room.cabinets} tủ, {room.shelves} kệ · {room.itemsCount}/{room.capacity} hiện vật
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full">
                    <div className={`h-2 w-full rounded-full ${progressTrackBg}`}>
                      <div 
                        className={`h-2 rounded-full ${progressBarBg}`}
                        style={{ width: `${room.usage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Detail Positions Table */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <th className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Vị trí</th>
                  <th className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Loại</th>
                  <th className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Số hiện vật</th>
                  <th className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Trạng thái</th>
                  <th className="px-4 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {filteredShelves.map((shelf) => {
                  const isFull = shelf.status === "Đầy"
                  const badgeBg = isFull ? "bg-[#842029] text-[#FFF5F5]" : "bg-[#135A42] text-[#E6F4EA]"
                  const isRowSelected = selectedShelfId === shelf.id

                  return (
                    <tr 
                      key={shelf.id}
                      onClick={() => setSelectedShelfId(shelf.id)}
                      className={`border-b border-[#E2E8F0] last:border-b-0 cursor-pointer transition-colors ${
                        isRowSelected ? "bg-[#E9ECF4]/30" : "hover:bg-slate-50/50"
                      }`}
                    >
                      <td className="px-4 py-2.5 text-xs font-semibold text-[#1E293B]">{shelf.location}</td>
                      <td className="px-4 py-2.5 text-xs font-medium text-[#475569]">{shelf.type}</td>
                      <td className="px-4 py-2.5 text-xs font-medium text-[#475569]">{shelf.itemsCount}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold inline-block ${badgeBg}`}>
                          {shelf.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end items-center gap-1.5">
                          <button 
                            onClick={() => {
                              setEditingShelf({ ...shelf })
                              setIsEditShelfOpen(true)
                            }}
                            className="p-1 hover:bg-slate-100 rounded-lg text-gray-500 transition-colors"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button 
                            onClick={() => {
                              if (confirm(`Bạn có chắc muốn xóa tủ/kệ tại: ${shelf.location}?`)) {
                                deleteShelf(shelf.id)
                              }
                            }}
                            className="p-1 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {filteredShelves.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-xs text-gray-500 font-medium">
                      Không tìm thấy vị trí nào phù hợp.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Room Dialog */}
        <Dialog open={isCreateRoomOpen} onOpenChange={setIsCreateRoomOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Tạo kho mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="room-name">Tên kho *</Label>
                  <Input
                    id="room-name"
                    value={roomForm.name}
                    onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
                    placeholder="Ví dụ: Kho A - Tầng"
                  />
                </div>
                <div>
                  <Label htmlFor="room-code">Mã ký hiệu kho</Label>
                  <Input
                    id="room-code"
                    value={roomForm.code}
                    onChange={(e) => setRoomForm({ ...roomForm, code: e.target.value })}
                    placeholder="Ví dụ: KHA"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="room-manager">Người quản lý / phụ trách</Label>
                <Input
                  id="room-manager"
                  value={roomForm.manager}
                  onChange={(e) => setRoomForm({ ...roomForm, manager: e.target.value })}
                  placeholder="Nhập tên người quản lý"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="room-cabinets">Số lượng tủ</Label>
                  <Input
                    id="room-cabinets"
                    type="number"
                    value={roomForm.cabinets}
                    onChange={(e) => setRoomForm({ ...roomForm, cabinets: Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="room-shelves">Số lượng kệ tủ</Label>
                  <Input
                    id="room-shelves"
                    type="number"
                    value={roomForm.shelves}
                    onChange={(e) => setRoomForm({ ...roomForm, shelves: Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="room-description">Mô tả chi tiết</Label>
                <Textarea
                  id="room-description"
                  value={roomForm.description}
                  onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })}
                  placeholder="Nhập mô tả về kho lưu trữ mới..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateRoomOpen(false)}>
                  Hủy
                </Button>
                <Button onClick={createRoom}>
                  <Save className="h-4 w-4 mr-1" />
                  Tạo kho
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Shelf Dialog */}
        <Dialog open={isCreateShelfOpen} onOpenChange={setIsCreateShelfOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Thêm tủ / kệ mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="shelf-location">Vị trí *</Label>
                <Input
                  id="shelf-location"
                  value={shelfForm.location}
                  onChange={(e) => setShelfForm({ ...shelfForm, location: e.target.value })}
                  placeholder="Ví dụ: Kho A / Tủ 03 / Kệ 01"
                />
              </div>
              <div>
                <Label htmlFor="shelf-type">Loại hiện vật</Label>
                <select
                  id="shelf-type"
                  value={shelfForm.type}
                  onChange={(e) => setShelfForm({ ...shelfForm, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#224584]"
                >
                  <option value="Tài liệu giấy">Tài liệu giấy</option>
                  <option value="Bản đồ cổ">Bản đồ cổ</option>
                  <option value="Gốm sứ">Gốm sứ</option>
                  <option value="Tranh sơn dầu">Tranh sơn dầu</option>
                  <option value="Hiện vật kim loại">Hiện vật kim loại</option>
                  <option value="Sách lật cổ">Sách lật cổ</option>
                  <option value="Hiện vật gỗ">Hiện vật gỗ</option>
                  <option value="Băng đĩa âm thanh">Băng đĩa âm thanh</option>
                  <option value="Đồ gỗ">Đồ gỗ</option>
                  <option value="Văn bản số hóa">Văn bản số hóa</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="shelf-items">Số hiện vật hiện có</Label>
                  <Input
                    id="shelf-items"
                    type="number"
                    value={shelfForm.itemsCount}
                    onChange={(e) => setShelfForm({ ...shelfForm, itemsCount: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label htmlFor="shelf-capacity">Sức chứa tối đa</Label>
                  <Input
                    id="shelf-capacity"
                    type="number"
                    value={shelfForm.capacity}
                    onChange={(e) => setShelfForm({ ...shelfForm, capacity: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="shelf-status">Trạng thái</Label>
                <select
                  id="shelf-status"
                  value={shelfForm.status}
                  onChange={(e) => setShelfForm({ ...shelfForm, status: e.target.value as any })}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#224584]"
                >
                  <option value="Còn chỗ">Còn chỗ</option>
                  <option value="Gần đầy">Gần đầy</option>
                  <option value="Đầy">Đầy</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateShelfOpen(false)}>
                  Hủy
                </Button>
                <Button onClick={createShelf}>
                  <Save className="h-4 w-4 mr-1" />
                  Thêm tủ/kệ
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Shelf Dialog */}
        <Dialog open={isEditShelfOpen} onOpenChange={setIsEditShelfOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cập nhật tủ / kệ</DialogTitle>
            </DialogHeader>
            {editingShelf && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-shelf-location">Vị trí *</Label>
                  <Input
                    id="edit-shelf-location"
                    value={editingShelf.location}
                    onChange={(e) => setEditingShelf({ ...editingShelf, location: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-shelf-type">Loại hiện vật</Label>
                  <select
                    id="edit-shelf-type"
                    value={editingShelf.type}
                    onChange={(e) => setEditingShelf({ ...editingShelf, type: e.target.value })}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#224584]"
                  >
                    <option value="Tài liệu giấy">Tài liệu giấy</option>
                    <option value="Bản đồ cổ">Bản đồ cổ</option>
                    <option value="Gốm sứ">Gốm sứ</option>
                    <option value="Tranh sơn dầu">Tranh sơn dầu</option>
                    <option value="Hiện vật kim loại">Hiện vật kim loại</option>
                    <option value="Sách lật cổ">Sách lật cổ</option>
                    <option value="Hiện vật gỗ">Hiện vật gỗ</option>
                    <option value="Băng đĩa âm thanh">Băng đĩa âm thanh</option>
                    <option value="Đồ gỗ">Đồ gỗ</option>
                    <option value="Văn bản số hóa">Văn bản số hóa</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-shelf-items">Số hiện vật hiện có</Label>
                    <Input
                      id="edit-shelf-items"
                      type="number"
                      value={editingShelf.itemsCount}
                      onChange={(e) => setEditingShelf({ ...editingShelf, itemsCount: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-shelf-capacity">Sức chứa tối đa</Label>
                    <Input
                      id="edit-shelf-capacity"
                      type="number"
                      value={editingShelf.capacity}
                      onChange={(e) => setEditingShelf({ ...editingShelf, capacity: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-shelf-status">Trạng thái</Label>
                  <select
                    id="edit-shelf-status"
                    value={editingShelf.status}
                    onChange={(e) => setEditingShelf({ ...editingShelf, status: e.target.value as any })}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#224584]"
                  >
                    <option value="Còn chỗ">Còn chỗ</option>
                    <option value="Gần đầy">Gần đầy</option>
                    <option value="Đầy">Đầy</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsEditShelfOpen(false)}>
                    Hủy
                  </Button>
                  <Button onClick={saveShelf}>
                    <Save className="h-4 w-4 mr-1" />
                    Lưu thay đổi
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Lịch sử hoạt động kho</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              <div className="border-l-2 border-[#224584] pl-3 py-1.5 text-xs">
                <span className="font-semibold text-gray-500">Hôm nay, 10:15</span>
                <p className="text-gray-800 font-medium mt-0.5">Thêm 15 tài liệu vào Kho A / Tủ 01 / Kệ 03</p>
              </div>
              <div className="border-l-2 border-amber-500 pl-3 py-1.5 text-xs">
                <span className="font-semibold text-gray-500">Hôm qua, 14:30</span>
                <p className="text-gray-800 font-medium mt-0.5">Cập nhật trạng thái Kho B / Tủ 03 / Kệ 02 thành "Đầy"</p>
              </div>
              <div className="border-l-2 border-[#224584] pl-3 py-1.5 text-xs">
                <span className="font-semibold text-gray-500">05/07/2026, 09:00</span>
                <p className="text-gray-800 font-medium mt-0.5">Tạo mới kho lưu trữ Kho D - Tầng</p>
              </div>
              <div className="border-l-2 border-[#2F6B38] pl-3 py-1.5 text-xs">
                <span className="font-semibold text-gray-500">01/07/2026, 16:45</span>
                <p className="text-gray-800 font-medium mt-0.5">Xuất kho 24 hiện vật gốm sứ sang gian trưng bày chính</p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setIsHistoryOpen(false)}>
                Đóng
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </LayoutDefault>
  )
}
